// LeaseholderRemovalPage — /send/leaseholder-removal route: send the Leaseholder
// Removal & Release Agreement to one tenancy's parties.
//
// The remove-only sibling of the Leaseholder Change agreement. Initiation is
// TENANCY-FIRST — staff pick a property + unit directly (there is no application
// picker and nobody joins). Picking a unit loads the tenancy context:
//   • "On the lease today": each current financially-responsible resident gets a
//     Remaining / Outgoing toggle (default Remaining) + an editable email. A
//     "+ Add person" escape hatch appends a manual, fully-editable row.
//   • Effective date, Lease date (prefilled; on renewed tenancies AppFolio may
//     only know the current term's start), premises address.
//   • Send → POST /api/leaseholder-removal/initiate. Residents sign in parallel;
//     WHP (the Lessor) countersigns last.
//
// The feature ships DORMANT (a legal document): when the backend's
// LEASEHOLDER_REMOVAL_SEND_ENABLED flag is off, the context read reports
// sendEnabled:false — we show a dismissable banner but KEEP the button enabled so
// staging behavior is testable (the initiate route 503s cleanly).

import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { SearchSelect } from '@/components/SearchSelect';
import { toast } from '@/components/Toast';
import { ApiError } from '@/lib/api';
import {
  listProperties,
  listUnits,
  type PropertyOption,
  type UnitOption,
  unitOptionLabel,
} from '@/hooks/useLeaseLifecycle';
import {
  getLeaseholderRemovalContext,
  initiateLeaseholderRemoval,
  leaseholderRemovalErrorDetail,
  LeaseholderRemovalNotEnabledError,
  type InitiateLeaseholderRemovalResult,
  type LeaseholderRemovalPerson,
} from '@/lib/leaseholderRemovalClient';

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/** One resident on the lease — staff mark Remaining vs Outgoing + fix the email.
 *  'appfolio' rows come from the tenancy (identity fixed, email editable);
 *  'manual' rows are the fully-editable escape hatch. */
interface ResidentRow {
  key: number;
  source: 'appfolio' | 'manual';
  firstName: string;
  lastName: string;
  email: string;
  outgoing: boolean;
}

let nextKey = 1;

const blankManualRow = (): ResidentRow => ({
  key: nextKey++,
  source: 'manual',
  firstName: '',
  lastName: '',
  email: '',
  outgoing: false,
});

function fullName(firstName: string, lastName: string): string {
  return `${firstName.trim()} ${lastName.trim()}`.trim();
}

export function LeaseholderRemovalPage(): React.JSX.Element {
  // --- Property/unit picker + context load ---
  const [properties, setProperties] = useState<PropertyOption[]>([]);
  const [propsLoading, setPropsLoading] = useState(true);
  const [propsError, setPropsError] = useState<string | null>(null);
  const [propertyId, setPropertyId] = useState('');
  const [units, setUnits] = useState<UnitOption[]>([]);
  const [unitsLoading, setUnitsLoading] = useState(false);
  const [unitsError, setUnitsError] = useState<string | null>(null);
  const [unitId, setUnitId] = useState('');
  const [ctxLoading, setCtxLoading] = useState(false);
  const [ctxError, setCtxError] = useState<string | null>(null);
  const [ctxLoaded, setCtxLoaded] = useState(false);

  // --- The working set ---
  const [residents, setResidents] = useState<ResidentRow[]>([]);
  const [effectiveDate, setEffectiveDate] = useState('');
  const [originalLeaseDate, setOriginalLeaseDate] = useState('');
  /** Whether AppFolio had a lease start date on file (drives the helper text). */
  const [leaseDateOnFile, setLeaseDateOnFile] = useState(true);
  const [propertyAddress, setPropertyAddress] = useState('');

  const [sendEnabled, setSendEnabled] = useState(true);
  const [dormantDismissed, setDormantDismissed] = useState(false);
  const [busy, setBusy] = useState(false);
  const [sendError, setSendError] = useState<{ message: string; detail: string[] } | null>(null);
  const [result, setResult] = useState<InitiateLeaseholderRemovalResult | null>(null);

  // Load the property list on mount.
  useEffect(() => {
    let cancelled = false;
    setPropsLoading(true);
    listProperties()
      .then((list) => {
        if (!cancelled) setProperties(list);
      })
      .catch((err) => {
        if (!cancelled) setPropsError(err instanceof Error ? err.message : 'Failed to load properties');
      })
      .finally(() => {
        if (!cancelled) setPropsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  // Load units whenever the property changes.
  useEffect(() => {
    if (!propertyId) {
      setUnits([]);
      setUnitId('');
      setUnitsError(null);
      return;
    }
    let cancelled = false;
    setUnitsLoading(true);
    setUnitsError(null);
    listUnits(propertyId)
      .then((list) => {
        if (cancelled) return;
        setUnits(list);
        setUnitId((prev) => (list.some((u) => u.unitId === prev) ? prev : (list[0]?.unitId ?? '')));
      })
      .catch((err) => {
        if (!cancelled) setUnitsError(err instanceof Error ? err.message : 'Failed to load units');
      })
      .finally(() => {
        if (!cancelled) setUnitsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [propertyId]);

  /** Reset the whole working set. */
  const clearWorkset = () => {
    setResidents([]);
    setEffectiveDate('');
    setOriginalLeaseDate('');
    setLeaseDateOnFile(true);
    setPropertyAddress('');
    setSendError(null);
    setResult(null);
  };

  // On unit pick, load the tenancy context. Everything below the picker is
  // unit-scoped, so a unit change resets it all.
  useEffect(() => {
    setCtxLoaded(false);
    setCtxError(null);
    clearWorkset();
    if (!propertyId || !unitId) return;
    let cancelled = false;
    setCtxLoading(true);
    getLeaseholderRemovalContext(propertyId, unitId)
      .then((ctx) => {
        if (cancelled) return;
        setResidents(
          ctx.residents.map((r) => ({
            key: nextKey++,
            source: 'appfolio' as const,
            firstName: r.firstName,
            lastName: r.lastName,
            email: r.email ?? '',
            outgoing: false,
          })),
        );
        setOriginalLeaseDate(ctx.leaseStartDate);
        setLeaseDateOnFile(!!ctx.leaseStartDate);
        setPropertyAddress(ctx.propertyAddress);
        setSendEnabled(ctx.sendEnabled);
        setCtxLoaded(true);
      })
      .catch((err) => {
        if (cancelled) return;
        setCtxError(
          err instanceof ApiError
            ? ((err.body as { error?: string } | null)?.error ??
                `Could not load the tenancy (HTTP ${err.status})`)
            : err instanceof Error
              ? err.message
              : 'Could not load the tenancy',
        );
      })
      .finally(() => {
        if (!cancelled) setCtxLoading(false);
      });
    return () => {
      cancelled = true;
    };
    // clearWorkset only issues setState calls — intentionally not a dep.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [propertyId, unitId]);

  const worksetReady = ctxLoaded;

  const updateResident = (key: number, patch: Partial<ResidentRow>) => {
    setResidents((prev) => prev.map((r) => (r.key === key ? { ...r, ...patch } : r)));
  };

  const remaining = residents.filter((r) => !r.outgoing);
  const outgoing = residents.filter((r) => r.outgoing);

  // Validation — every reason renders inline next to the (disabled) send button.
  const reasons: string[] = [];
  if (worksetReady) {
    if (remaining.length === 0)
      reasons.push('At least one resident must stay on the lease (mark them Remaining).');
    if (outgoing.length === 0)
      reasons.push('Mark at least one resident as Outgoing — this agreement removes someone from the lease.');
    residents.forEach((r, idx) => {
      const label = fullName(r.firstName, r.lastName) || `Resident ${idx + 1}`;
      if (!r.firstName.trim()) reasons.push(`Resident ${idx + 1} needs a first name.`);
      if (!EMAIL_RE.test(r.email.trim())) reasons.push(`${label} needs a valid email.`);
    });
    if (!effectiveDate) reasons.push('Set the effective date.');
    if (!originalLeaseDate) reasons.push('Set the lease date.');
    if (!propertyAddress.trim()) reasons.push('Enter the property address.');
  }
  const canSend = !busy && worksetReady && reasons.length === 0;

  const toPerson = (r: { firstName: string; lastName: string; email: string }): LeaseholderRemovalPerson => ({
    firstName: r.firstName.trim(),
    lastName: r.lastName.trim(),
    email: r.email.trim(),
  });

  const handleSend = async () => {
    if (!canSend || !propertyId || !unitId) return;
    setBusy(true);
    setSendError(null);
    try {
      const res = await initiateLeaseholderRemoval({
        propertyId,
        unitId,
        propertyAddress: propertyAddress.trim(),
        originalLeaseDate,
        effectiveDate,
        remaining: remaining.map(toPerson),
        outgoing: outgoing.map(toPerson),
      });
      setResult(res);
      toast.success(
        `Sent the leaseholder removal agreement — ${res.emailed} signing ${res.emailed === 1 ? 'link' : 'links'} emailed.`,
        { title: 'Leaseholder removal' },
      );
    } catch (err) {
      if (err instanceof LeaseholderRemovalNotEnabledError) {
        setSendError({ message: err.message, detail: [] });
      } else {
        const parsed = leaseholderRemovalErrorDetail(err);
        setSendError(
          parsed ?? {
            message:
              err instanceof Error ? err.message : 'Could not send the leaseholder removal agreement',
            detail: [],
          },
        );
      }
    } finally {
      setBusy(false);
    }
  };

  // --- Sent: show the created group + roster; the form is done. ---
  if (result) {
    return (
      <div style={{ maxWidth: 720 }}>
        <div style={{ marginBottom: 'var(--space-7)' }}>
          <h1 style={{ fontSize: 'var(--text-xl)', fontWeight: 'var(--weight-bold)', color: 'var(--fg)', margin: 0 }}>
            Leaseholder removal
          </h1>
        </div>
        <section className="card">
          <div className="card-header">
            <h3 className="card-title">Agreement sent</h3>
          </div>
          <div className="stack-sm">
            <p style={{ fontSize: 'var(--text-sm)', color: 'var(--fg-muted)', margin: 0 }}>
              {result.emailed} signing{' '}
              {result.emailed === 1 ? 'link' : 'links'} emailed. All resident parties sign in
              parallel; Welcome Home Properties countersigns once every resident has signed.
            </p>
            <div className="table-wrap">
              <table className="table">
                <thead>
                  <tr>
                    <th>Role</th>
                    <th>Name</th>
                    <th>Email</th>
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {result.submitters.map((s) => (
                    <tr key={s.role}>
                      <td>{s.role}</td>
                      <td>{s.name ?? '—'}</td>
                      <td>{s.email ?? '—'}</td>
                      <td>
                        <span className="pill" data-tone={s.status === 'sent' || s.status === 'completed' ? 'success' : 'neutral'}>
                          {s.status ? s.status.charAt(0).toUpperCase() + s.status.slice(1) : '—'}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <p style={{ fontSize: 'var(--text-sm)', color: 'var(--fg-muted)', margin: 0 }}>
              Track it on the <Link to="/requests">Requests page</Link>.
            </p>
          </div>
        </section>
      </div>
    );
  }

  return (
    <div style={{ maxWidth: 720 }}>
      <div style={{ marginBottom: 'var(--space-7)' }}>
        <h1 style={{ fontSize: 'var(--text-xl)', fontWeight: 'var(--weight-bold)', color: 'var(--fg)', margin: 0 }}>
          Leaseholder removal
        </h1>
        <p style={{ fontSize: 'var(--text-sm)', color: 'var(--fg-muted)', marginTop: 'var(--space-2)' }}>
          Send a Leaseholder Removal &amp; Release Agreement to remove an outgoing resident from an
          existing lease. Pick a property and unit, mark each current resident Remaining or Outgoing,
          and the outgoing residents are released as of the effective date once they have moved out
          and returned their keys. At least one resident must remain. Every party signs, then WHP
          countersigns.
        </p>
      </div>

      {ctxLoaded && !sendEnabled && !dormantDismissed && (
        <div
          className="alert"
          data-tone="info"
          role="note"
          style={{ marginBottom: 'var(--space-5)', display: 'flex', alignItems: 'flex-start', gap: 'var(--space-3)' }}
        >
          <div className="alert-body" style={{ flex: 1 }}>
            <div className="alert-title">Sends are dormant</div>
            Leaseholder removal sends are dormant (<code>LEASEHOLDER_REMOVAL_SEND_ENABLED</code> off)
            — the send will be refused.
          </div>
          <button
            type="button"
            className="btn"
            data-variant="ghost"
            data-size="sm"
            data-icon-only
            aria-label="Dismiss dormant notice"
            onClick={() => setDormantDismissed(true)}
          >
            ×
          </button>
        </div>
      )}

      <div className="stack">
        {/* Property/unit picker */}
        <section className="card">
          <div className="card-header">
            <h3 className="card-title">Property &amp; unit</h3>
          </div>
          <div className="stack-sm">
            {propsError && (
              <div className="alert" data-tone="danger" role="alert">
                <div className="alert-body">{propsError}</div>
              </div>
            )}
            <div className="field">
              <label htmlFor="prop-select">Property</label>
              <SearchSelect
                id="prop-select"
                options={properties.map((p) => ({
                  value: p.propertyId,
                  label: p.name,
                  sublabel: p.address,
                }))}
                value={propertyId}
                onChange={setPropertyId}
                disabled={propsLoading}
                placeholder={propsLoading ? 'Loading…' : 'Select or search by name or address…'}
                emptyText="No matching properties"
              />
            </div>
            <div className="field">
              <label htmlFor="unit-select">Unit</label>
              <select
                id="unit-select"
                className="input"
                value={unitId}
                onChange={(e) => setUnitId(e.target.value)}
                disabled={!propertyId || unitsLoading}
              >
                <option value="">
                  {!propertyId
                    ? 'Select a property first…'
                    : unitsLoading
                      ? 'Loading units…'
                      : units.length === 0
                        ? 'No units found for this property'
                        : 'Select a unit…'}
                </option>
                {units.map((u) => (
                  <option key={u.unitId} value={u.unitId}>
                    {unitOptionLabel(u)}
                    {u.address ? ` — ${u.address}` : ''}
                  </option>
                ))}
              </select>
              {unitsError && <span className="helper" data-state="error">{unitsError}</span>}
              {ctxError && <span className="helper" data-state="error">{ctxError}</span>}
              {ctxLoading && <span className="helper">Loading the tenancy…</span>}
            </div>
          </div>
        </section>

        {/* Current residents — Remaining vs Outgoing */}
        <section className="card">
          <div className="card-header">
            <h3 className="card-title">On the lease today</h3>
          </div>
          {!worksetReady ? (
            <p style={{ fontSize: 'var(--text-sm)', color: 'var(--fg-muted)', margin: 0 }}>
              Pick a property + unit to load the current residents.
            </p>
          ) : (
            <div className="stack-sm">
              {residents.length === 0 && (
                <div className="alert" data-tone="warning" role="note" style={{ marginBottom: 'var(--space-3)' }}>
                  <div className="alert-body">
                    AppFolio shows no current financially-responsible residents for this unit — a
                    leaseholder removal needs an existing tenancy. Use “+ Add person” to enter them
                    by hand.
                  </div>
                </div>
              )}
              {residents.length > 0 && (
                <p style={{ fontSize: 'var(--text-xs)', color: 'var(--fg-muted)', margin: 0 }}>
                  Everyone listed signs the agreement. Mark each resident Remaining (stays on the
                  lease) or Outgoing (removed and released as of the effective date). Each needs a
                  valid email.
                </p>
              )}
              <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-3)' }}>
                {residents.map((r, idx) => {
                  const email = r.email.trim();
                  const emailInvalid = !!email && !EMAIL_RE.test(email);
                  const name = fullName(r.firstName, r.lastName) || `Resident ${idx + 1}`;
                  return (
                    <div key={r.key} style={{ display: 'flex', gap: 'var(--space-3)', alignItems: 'flex-start' }}>
                      {r.source === 'manual' ? (
                        <>
                          <input
                            type="text"
                            className="input"
                            style={{ flex: 1 }}
                            placeholder="First name"
                            value={r.firstName}
                            onChange={(e) => updateResident(r.key, { firstName: e.target.value })}
                            aria-label={`Resident ${idx + 1} first name`}
                          />
                          <input
                            type="text"
                            className="input"
                            style={{ flex: 1 }}
                            placeholder="Last name"
                            value={r.lastName}
                            onChange={(e) => updateResident(r.key, { lastName: e.target.value })}
                            aria-label={`Resident ${idx + 1} last name`}
                          />
                        </>
                      ) : (
                        <div style={{ flex: 1, minWidth: 0, paddingTop: 6 }}>
                          <span style={{ fontWeight: 'var(--weight-medium)', color: 'var(--fg)' }}>{name}</span>
                        </div>
                      )}
                      <div style={{ flex: 1.4, display: 'flex', flexDirection: 'column', gap: 2 }}>
                        <input
                          type="email"
                          className="input"
                          placeholder="resident@example.com"
                          value={r.email}
                          onChange={(e) => updateResident(r.key, { email: e.target.value })}
                          aria-label={`${name} email`}
                          aria-invalid={emailInvalid}
                        />
                        {emailInvalid && <span className="helper" data-state="error">Enter a valid email.</span>}
                        {!email && <span className="helper" data-state="error">Email required to send.</span>}
                      </div>
                      <div role="group" aria-label={`${name} — remaining or outgoing`} style={{ display: 'flex', gap: 'var(--space-1)' }}>
                        <button
                          type="button"
                          className="btn"
                          data-size="sm"
                          data-variant={r.outgoing ? 'ghost' : 'secondary'}
                          aria-pressed={!r.outgoing}
                          onClick={() => updateResident(r.key, { outgoing: false })}
                        >
                          Remaining
                        </button>
                        <button
                          type="button"
                          className="btn"
                          data-size="sm"
                          data-variant={r.outgoing ? 'secondary' : 'ghost'}
                          aria-pressed={r.outgoing}
                          onClick={() => updateResident(r.key, { outgoing: true })}
                        >
                          Outgoing
                        </button>
                      </div>
                      {r.source === 'manual' && (
                        <button
                          type="button"
                          className="btn"
                          data-variant="ghost"
                          data-size="sm"
                          data-icon-only
                          aria-label={`Remove ${name}`}
                          onClick={() => setResidents((prev) => prev.filter((x) => x.key !== r.key))}
                        >
                          ×
                        </button>
                      )}
                    </div>
                  );
                })}
                <button
                  type="button"
                  className="btn"
                  data-variant="secondary"
                  data-size="sm"
                  onClick={() => setResidents((prev) => [...prev, blankManualRow()])}
                  style={{ alignSelf: 'flex-start' }}
                >
                  + Add person
                </button>
              </div>
            </div>
          )}
        </section>

        {/* Dates + premises */}
        <section className="card">
          <div className="card-header">
            <h3 className="card-title">Dates &amp; premises</h3>
          </div>
          {!worksetReady ? (
            <p style={{ fontSize: 'var(--text-sm)', color: 'var(--fg-muted)', margin: 0 }}>
              Pick a property + unit first.
            </p>
          ) : (
            <div className="stack-sm">
              <div className="field">
                <label htmlFor="lr-effective">Effective date</label>
                <input
                  id="lr-effective"
                  type="date"
                  className="input"
                  value={effectiveDate}
                  onChange={(e) => setEffectiveDate(e.target.value)}
                  aria-invalid={!effectiveDate}
                  aria-describedby="lr-effective-hint"
                />
                <span id="lr-effective-hint" className="helper" data-state={!effectiveDate ? 'error' : undefined}>
                  The removal takes effect at 11:59 p.m. on this date, once the outgoing residents
                  have moved out and returned their keys.
                </span>
              </div>
              <div className="field">
                <label htmlFor="lr-lease">Lease date</label>
                <input
                  id="lr-lease"
                  type="date"
                  className="input"
                  value={originalLeaseDate}
                  onChange={(e) => setOriginalLeaseDate(e.target.value)}
                  aria-invalid={!originalLeaseDate}
                  aria-describedby="lr-lease-hint"
                />
                <span
                  id="lr-lease-hint"
                  className="helper"
                  data-state={!originalLeaseDate ? 'error' : undefined}
                >
                  {leaseDateOnFile
                    ? 'Prefilled from AppFolio. On renewed tenancies AppFolio may only show the current term’s start — confirm against the signed lease.'
                    : 'Not on file in AppFolio — enter the date of the signed lease.'}
                </span>
              </div>
              <div className="field">
                <label htmlFor="lr-address">Property address</label>
                <input
                  id="lr-address"
                  type="text"
                  className="input"
                  value={propertyAddress}
                  onChange={(e) => setPropertyAddress(e.target.value)}
                  aria-invalid={!propertyAddress.trim()}
                />
                <span className="helper">
                  Prefilled from AppFolio — this names the premises in the agreement.
                </span>
              </div>
            </div>
          )}
        </section>

        {/* Action */}
        <section className="card">
          <div className="stack-sm">
            <p style={{ fontSize: 'var(--text-sm)', color: 'var(--fg-muted)', margin: 0 }}>
              Sends the agreement to every remaining and outgoing resident for signature (all in
              parallel); WHP countersigns last. Track it on the Requests page.
            </p>
            {sendError && (
              <div className="alert" data-tone="danger" role="alert">
                <div className="alert-body">
                  <div className="alert-title">{sendError.message}</div>
                  {sendError.detail.length > 0 && (
                    <ul style={{ margin: 'var(--space-2) 0 0', paddingLeft: 'var(--space-5)' }}>
                      {sendError.detail.map((d, i) => (
                        <li key={i}>{d}</li>
                      ))}
                    </ul>
                  )}
                </div>
              </div>
            )}
            {worksetReady && reasons.length > 0 && (
              <ul
                style={{
                  margin: 0,
                  paddingLeft: 'var(--space-5)',
                  fontSize: 'var(--text-sm)',
                  color: 'var(--fg-muted)',
                }}
              >
                {reasons.map((reason, i) => (
                  <li key={i}>{reason}</li>
                ))}
              </ul>
            )}
            <button
              type="button"
              className="btn"
              onClick={() => void handleSend()}
              disabled={!canSend}
              style={{ alignSelf: 'flex-start', marginTop: 'var(--space-2)' }}
            >
              {busy
                ? 'Sending…'
                : `Send leaseholder removal agreement${
                    worksetReady && reasons.length === 0
                      ? ` to ${remaining.length + outgoing.length} →`
                      : ' →'
                  }`}
            </button>
          </div>
        </section>
      </div>
    </div>
  );
}
